// src/utils/dateNormalization.ts
/**
 * ✅ CRITICAL: Redux requires serializable state
 * This helper ensures all Date objects are converted to ISO strings
 */

import { TreeNodeType } from '../services/ApiService';

/**
 * Normalize a single date value to ISO string
 */
export const normalizeDate = (date: Date | string | undefined | null): string | undefined => {
  if (!date) return undefined;

  try {
    if (typeof date === 'string') {
      const parsed = new Date(date);

      return Number.isNaN(parsed.getTime()) ? undefined : date;
    }

    if (date instanceof Date) {
      return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
    }

    return undefined;
  } catch (error) {
    console.error('Date normalization error:', error);

    return undefined;
  }
};

/**
 * Normalize tree data recursively - converts all Date objects to ISO strings
 * This prevents Redux serialization warnings
 */
export const normalizeTreeData = (data: any[]): TreeNodeType[] => data.map((item) => {
  // Inline implementation to avoid circular dependency
  const normalized: TreeNodeType = {
    ...item,
    data: item.data ? {
      ...item.data,
      createdAt: normalizeDate(item.data.createdAt),
      updatedAt: normalizeDate(item.data.updatedAt),
    } : undefined,
  };

  // Recursively normalize children
  if (item.children && Array.isArray(item.children)) {
    normalized.children = normalizeTreeData(item.children);
  }

  return normalized;
});

/**
 * Normalize a single tree item (convenience function)
 */
export const normalizeTreeItem = (item: any): TreeNodeType => normalizeTreeData([item])[0];

/**
 * Format ISO string or Date for display (DD.MM.YYYY)
 */
export const formatDateForDisplay = (date: Date | string | undefined): string => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    if (Number.isNaN(dateObj.getTime())) return '';

    const day = dateObj.getDate().toString().padStart(2, '0');
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const year = dateObj.getFullYear();

    return `${day}.${month}.${year}`;
  } catch (error) {
    console.error('Date formatting error:', error);

    return '';
  }
};

/**
 * Get current timestamp as ISO string (Redux-safe)
 */
export const getCurrentTimestamp = (): string => new Date().toISOString();

/**
 * Check if a value is a Date object (helper for debugging)
 */
export const isDateObject = (value: any): boolean => value instanceof Date;

/**
 * Deep scan object for Date instances (debugging tool)
 * Use in development to find Date objects in Redux state
 */
export const findDateObjectsInState = (obj: any, path = ''): string[] => {
  const dateObjects: string[] = [];

  if (obj === null || obj === undefined) {
    return dateObjects;
  }

  if (isDateObject(obj)) {
    dateObjects.push(path || 'root');

    return dateObjects;
  }

  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      dateObjects.push(...findDateObjectsInState(item, `${path}[${index}]`));
    });
  } else if (typeof obj === 'object') {
    Object.keys(obj).forEach((key) => {
      const newPath = path ? `${path}.${key}` : key;
      dateObjects.push(...findDateObjectsInState(obj[key], newPath));
    });
  }

  return dateObjects;
};

export default {
  normalizeDate,
  normalizeTreeData,
  normalizeTreeItem,
  formatDateForDisplay,
  getCurrentTimestamp,
  isDateObject,
  findDateObjectsInState,
};
